import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';
import { Blueprint } from 'src/app/models/blueprints/blueprint';
import { KeyValueMap } from 'src/app/models/key-value-map';
import { RouterSegments } from 'src/app/models/router-segments';
import { Solution } from 'src/app/models/service/solution';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import { InstancesService } from 'src/app/services/api/instances.service';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { Instance } from 'src/app/models/instance/instance';
import { finalize } from 'rxjs/operators';
import { Sandbox } from 'src/app/models/sandbox';
import { LineOfBusiness } from 'src/app/models/line-of-business';

@Component({
    selector: 'app-my-sandbox',
    templateUrl: './my-sandbox.component.html',
    styleUrls: ['./my-sandbox.component.scss'],
    standalone: false
})
export class MySandboxComponent implements OnInit {

  RouterSegments = RouterSegments;
  tenant: string;

  lineOfBusinessToSandboxCountMap: KeyValueMap<string, number> = {};
  allSandboxesCount: number;

  solutionsMap: KeyValueMap<string, Solution> = {};
  demoBlueprintsMap: KeyValueMap<string, Blueprint> = {};
  sandboxes: Sandbox[] = [];
  filteredSandboxes: Sandbox[] = [];

  dataLoaded = false;

  filters: Set<string> = new Set();

  lineOfBusinessMap: KeyValueMap<string, LineOfBusiness> = {};
  private destroyRef = inject(DestroyRef);

  constructor(private authService: AuthService, private solutionsService: SolutionsService,
              private blueprintsService: BlueprintsService, private instancesService: InstancesService) {
  }

  ngOnInit(): void {
    this.tenant = this.authService.principal.provider;
    this.loadLineOfBusinesses();
    this.loadData();
  }

  loadData(): void {
    combineLatest([this.solutionsService.getSolutions(), this.blueprintsService.getBlueprints(), this.instancesService.getInstances()])
      .pipe(
        finalize(() => {
          this.dataLoaded = true;
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(([solutions, blueprints, instances]) => {
        const solutionsMap = {};
        solutions.forEach(solution => {
          solutionsMap[solution.id] = solution;
          solution.lineOfBusiness.forEach(lineOfBusiness => {
            this.lineOfBusinessToSandboxCountMap[lineOfBusiness] = 0;
          });
        });

        const demoBlueprints = blueprints.filter(blueprint => blueprint.demo)
        .reduce((acc, blueprint) => {
          acc.add(blueprint.id);
          return acc;
        }, new Set());

        instances.filter(instance => demoBlueprints.has(instance.blueprintId))
        .forEach(instance => {
          const solution = solutionsMap[instance.solutionId];
          // Engine's db is not always
          if (solution) {
            solution.lineOfBusiness.forEach(lineOfBusiness => this.lineOfBusinessToSandboxCountMap[lineOfBusiness] += 1);
            this.sandboxes.push({
              instance,
              service: solution
            });
          }
        });

        this.allSandboxesCount = Object.values(this.lineOfBusinessToSandboxCountMap).reduce((sum, count) => sum += count, 0);
        this.applyFilter();
      });
  }

  clearFilter(): void {
    this.filters.clear();
    this.applyFilter();
  }

  selectFilter(filter: string): void {
    if (this.filters.has(filter)) {
      this.filters.delete(filter);
    } else {
      this.filters.add(filter);
    }
    this.applyFilter();
  }

  private loadLineOfBusinesses(): void {
    this.solutionsService.getLineOfBusinesses()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(lineOfBusinesses => {
      lineOfBusinesses.forEach(lineOfBusiness => {
        this.lineOfBusinessMap[lineOfBusiness.name] = lineOfBusiness;
      });
    });
  }

  private applyFilter(): void {
    if (this.filters.size === 0) {
      this.filteredSandboxes = this.sandboxes;
    } else {
      this.filteredSandboxes = this.sandboxes
      .filter(sandbox => sandbox.service.lineOfBusiness.some(lineOfBusiness => this.filters.has(lineOfBusiness)));
    }
  }
}
